import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"

/**
 * OKS-387 — W19 dormant inflection band (UX Spec §2/§3).
 *
 * The first element of `Creator/Profile` when the creator is dormant:
 * a warm "welcome back" moment instead of a cold/broken page (PRD §0 /
 * #17 / G18). Heading "{BRAND} MISSED YOU" (brand uppercased via copy)
 * over a "Welcome back, {first name}." sub-line.
 *
 * Passive reflection of derived state — no CTA, no dismiss control
 * (the dismissible nudge lives on the separate missions-home surface,
 * PRD Q6). The band auto-disappears on next activity.
 *
 * Meaning is conveyed by TEXT (heading + sub-line), so the dormant
 * state is announced to screen readers regardless of styling (a11y,
 * UX Spec §6).
 *
 * # Locked testid
 *
 *   - `dormant-inflection-band` — wrapper
 */
interface DormantInflectionBandProps {
  /** Current brand display name (heading). */
  brandName: string
  /** Creator's first name (sub-line). */
  firstName: string
}

export function DormantInflectionBand({
  brandName,
  firstName,
}: DormantInflectionBandProps) {
  const { t } = useTranslation()

  return (
    <Card
      data-testid="dormant-inflection-band"
      className="bg-accent text-accent-foreground border-none"
    >
      <CardContent className="flex flex-col gap-1 py-5 text-center">
        <p className="text-lg font-semibold tracking-wide break-words">
          {t("xp.profile.identity.missed_you", { brand: brandName })}
        </p>
        <p className="text-sm">
          {t("xp.profile.identity.welcome_back", { name: firstName })}
        </p>
      </CardContent>
    </Card>
  )
}
